import { useLanguage } from "@/context/LanguageContext";
import { FileText, Send, ShoppingCart, BarChart3, ArrowRight } from "lucide-react";

export function HowItWorksSection() {
  const { t } = useLanguage();

  const steps = [
    {
      num: "01",
      icon: FileText,
      key: "createQuote",
      image: "/images/quote_management.png",
      color: "from-sky-500 to-cyan-500",
    },
    {
      num: "02",
      icon: Send,
      key: "sendQuote",
      image: "/images/communication_channels.png",
      color: "from-emerald-500 to-teal-500",
    },
    {
      num: "03",
      icon: ShoppingCart,
      key: "getOrder",
      image: "/images/order_factory.png",
      color: "from-amber-500 to-orange-500",
    },
    {
      num: "04",
      icon: BarChart3,
      key: "trackAnalyze",
      image: "/images/analytics_dashboard.png",
      color: "from-violet-500 to-purple-500",
    },
  ];

  return (
    <section data-testid="howitworks-section" className="py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#22A7D1]">
            {t("howItWorks.overline")}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-bold text-slate-900">
            {t("howItWorks.title")}
          </h2>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-slate-500">
            {t("howItWorks.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <div key={i} className="relative group" data-testid={`step-${step.num}`}>
                {/* Connector arrow (not on last) */}
                {i < steps.length - 1 && (
                  <div className="hidden lg:flex absolute top-16 -right-3 z-10 w-6 h-6 items-center justify-center">
                    <ArrowRight className="w-5 h-5 text-slate-300" />
                  </div>
                )}

                <div className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-slate-300 hover:shadow-lg transition-all h-full">
                  {/* Step number + icon */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-md`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-3xl font-black text-slate-100">{step.num}</span>
                  </div>

                  <h3 className="text-base font-bold text-slate-900 mb-1">
                    {t(`howItWorks.${step.key}`)}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed mb-4">
                    {t(`howItWorks.${step.key}Desc`)}
                  </p>

                  {/* Illustration */}
                  <div className="rounded-xl overflow-hidden border border-slate-100 bg-slate-50">
                    <img
                      src={step.image}
                      alt={`EntegrePanel - ${t(`howItWorks.${step.key}`)}`}
                      width="400"
                      height="280"
                      className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
